<script setup>
// NOTICE: KAPTION-INTEGRATION - Full-page WhatsApp Template Create Page
//
// ═══════════════════════════════════════════════════════════════════════════
// WhatsApp Cloud API — Template Component Rules & Limits
// (Source: Meta Business Management API — Message Template Components)
// These constants are reused by the authentication-template workflow.
// ═══════════════════════════════════════════════════════════════════════════
//
// TEMPLATE NAME
//   Pattern : ^[a-z][a-z0-9_]*$   (lowercase, digits, underscores; must start with letter)
//   Max len : 512 characters
//
// HEADER — TEXT
//   Max len : 60 characters
//   Variables : 1 max
//
// HEADER — MEDIA
//   IMAGE    : max 5 MB   (jpg, jpeg, png)
//   VIDEO    : max 16 MB  (mp4)
//   DOCUMENT : max 100 MB (pdf)
//
// BODY
//   Max len    : 1 024 characters
//   Variables  : 15 max
//   Formatting : bold, italic, strikethrough, monospace allowed
//   Restriction: no tabs, no 4+ consecutive spaces
//
// FOOTER
//   Max len   : 60 characters
//   Variables : none (plain text only)
//
// BUTTONS  (max 10 total; max 2 URL, max 1 PHONE, max 10 QUICK_REPLY)
//   Button text (all types) : 25 characters
//   URL button URL          : 2 000 characters  (1 variable allowed at end)
//   Phone number            : 20 characters      (international format with +)
//
// ═══════════════════════════════════════════════════════════════════════════
import { ref, computed, reactive, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { convertEmojiShortcodes } from 'shared/helpers/emojiShortcodes';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import KaptionMediaUpload from 'dashboard/components-next/Campaigns/KaptionMediaUpload.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

// Gate: redirect if feature flag is disabled
if (!isKaptionFlagEnabled('KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT')) {
  router.replace({ name: 'campaigns_whatsapp_index' });
}

// Get all WhatsApp inboxes then filter for Cloud API only
const allWhatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const whatsAppCloudInboxes = computed(() =>
  (allWhatsAppInboxes.value || []).filter(
    inbox => inbox.provider === 'whatsapp_cloud'
  )
);

const selectedInboxId = ref(null);
const bodyTextarea = ref(null);
const hasMultipleInboxes = computed(
  () => whatsAppCloudInboxes.value.length > 1
);
const hasNoInbox = computed(() => whatsAppCloudInboxes.value.length === 0);

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt_BR', label: 'Portuguese (BR)' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh_CN', label: 'Chinese (Simplified)' },
  { code: 'zh_TW', label: 'Chinese (Traditional)' },
  { code: 'ru', label: 'Russian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
];

const form = reactive({
  name: '',
  language: 'en',
  headerType: 'none',
  headerText: '',
  headerMediaUrl: '',
  body: '',
  footer: '',
  buttons: [],
});

const uiFlags = computed(() => store.getters['whatsappTemplates/getUIFlags']);

// ── WhatsApp limits (keep in sync with comment block above) ──
const LIMITS = {
  NAME_MAX: 512,
  HEADER_TEXT_MAX: 60,
  HEADER_VARS_MAX: 1,
  BODY_MAX: 1024,
  BODY_VARS_MAX: 15,
  FOOTER_MAX: 60,
  BUTTON_TEXT_MAX: 25,
  BUTTON_URL_MAX: 2000,
  BUTTON_PHONE_MAX: 20,
};

// ── Detected template variables (must precede validation computeds) ──
const detectedBodyParams = computed(() => {
  if (!form.body) return [];
  const matches = form.body.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
});

const detectedHeaderParams = computed(() => {
  if (!form.headerText || form.headerType !== 'text') return [];
  const matches =
    form.headerText.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
});

// ── Field-level validation ──
const nameError = computed(() => {
  if (!form.name) return '';
  if (!/^[a-z][a-z0-9_]*$/.test(form.name)) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.NAME_HELP');
  }
  if (form.name.length > LIMITS.NAME_MAX) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.NAME_TOO_LONG', {
      max: LIMITS.NAME_MAX,
    });
  }
  return '';
});

const headerTextError = computed(() => {
  if (form.headerType !== 'text' || !form.headerText) return '';
  if (form.headerText.length > LIMITS.HEADER_TEXT_MAX) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.HEADER_TEXT_TOO_LONG', {
      max: LIMITS.HEADER_TEXT_MAX,
    });
  }
  return '';
});

const headerVarsError = computed(() => {
  if (detectedHeaderParams.value.length > LIMITS.HEADER_VARS_MAX) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.HEADER_VARS_EXCEEDED', {
      max: LIMITS.HEADER_VARS_MAX,
    });
  }
  if (form.headerType === 'text' && form.headerText) {
    const trimmed = form.headerText.trim();
    const startsWithVar = /^\{\{[a-zA-Z_]\w*\}\}/.test(trimmed);
    const endsWithVar = /\{\{[a-zA-Z_]\w*\}\}$/.test(trimmed);
    if (startsWithVar || endsWithVar) {
      return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.BODY_VARS_POSITION');
    }
  }
  return '';
});

const bodyError = computed(() => {
  if (!form.body) return '';
  if (form.body.length > LIMITS.BODY_MAX) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.BODY_TOO_LONG', {
      max: LIMITS.BODY_MAX,
    });
  }
  return '';
});

const bodyVarsError = computed(() => {
  if (detectedBodyParams.value.length > LIMITS.BODY_VARS_MAX) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.BODY_VARS_EXCEEDED', {
      max: LIMITS.BODY_VARS_MAX,
    });
  }
  return '';
});

const bodyVarsPositionError = computed(() => {
  if (!form.body) return '';
  const trimmed = form.body.trim();
  const startsWithVar = /^\{\{[a-zA-Z_]\w*\}\}/.test(trimmed);
  const endsWithVar = /\{\{[a-zA-Z_]\w*\}\}$/.test(trimmed);
  if (startsWithVar || endsWithVar) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.BODY_VARS_POSITION');
  }
  return '';
});

const footerError = computed(() => {
  if (!form.footer) return '';
  if (form.footer.length > LIMITS.FOOTER_MAX) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.FOOTER_TOO_LONG', {
      max: LIMITS.FOOTER_MAX,
    });
  }
  return '';
});

const buttonErrors = computed(() =>
  form.buttons.map(btn => {
    const errors = {};
    if (btn.text && btn.text.length > LIMITS.BUTTON_TEXT_MAX) {
      errors.text = t(
        'WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.MAX_CHARACTERS',
        { max: LIMITS.BUTTON_TEXT_MAX }
      );
    }
    if (
      btn.type === 'URL' &&
      btn.url &&
      btn.url.length > LIMITS.BUTTON_URL_MAX
    ) {
      errors.url = t(
        'WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.MAX_CHARACTERS',
        { max: LIMITS.BUTTON_URL_MAX }
      );
    }
    if (
      btn.type === 'PHONE_NUMBER' &&
      btn.phone_number &&
      btn.phone_number.length > LIMITS.BUTTON_PHONE_MAX
    ) {
      errors.phone = t(
        'WHATSAPP_TEMPLATE_MGMT.CREATE.VALIDATION.MAX_CHARACTERS',
        { max: LIMITS.BUTTON_PHONE_MAX }
      );
    }
    return errors;
  })
);

const hasAnyValidationError = computed(() => {
  if (nameError.value) return true;
  if (headerTextError.value || headerVarsError.value) return true;
  if (bodyError.value || bodyVarsError.value || bodyVarsPositionError.value)
    return true;
  if (footerError.value) return true;
  if (buttonErrors.value.some(e => Object.keys(e).length > 0)) return true;
  return false;
});

const isFormValid = computed(() => {
  return (
    form.name &&
    form.language &&
    form.body &&
    selectedInboxId.value &&
    !hasAnyValidationError.value &&
    !uiFlags.value.isCreating
  );
});

// Preview data based on form
const previewTemplate = computed(() => {
  const components = [];

  if (form.headerType === 'text' && form.headerText) {
    components.push({
      type: 'HEADER',
      format: 'TEXT',
      text: form.headerText,
    });
  } else if (['image', 'video', 'document'].includes(form.headerType)) {
    const header = {
      type: 'HEADER',
      format: form.headerType.toUpperCase(),
    };
    if (form.headerMediaUrl) {
      header.example = { header_handle: [form.headerMediaUrl] };
    }
    components.push(header);
  }

  if (form.body) {
    components.push({ type: 'BODY', text: form.body });
  }

  if (form.footer) {
    components.push({ type: 'FOOTER', text: form.footer });
  }

  if (form.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(b => ({
        type: b.type.toUpperCase(),
        text: b.text,
        url: b.url,
        phone_number: b.phone_number,
      })),
    });
  }

  return {
    name: form.name || 'untitled_template',
    language: form.language,
    category: 'MARKETING',
    status: 'DRAFT',
    components,
  };
});

const hasPreviewContent = computed(() => {
  return form.body || form.footer || form.headerType !== 'none';
});

const addButton = () => {
  if (form.buttons.length < 3) {
    form.buttons.push({
      type: 'QUICK_REPLY',
      text: '',
      url: '',
      phone_number: '',
    });
  }
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const handleCancel = () => {
  const returnTo = route.query.returnTo;
  if (returnTo === 'direct_create') {
    router.push({ name: 'campaigns_direct_create' });
  } else if (returnTo === 'direct_send') {
    router.push({ name: 'campaigns_direct_send' });
  } else {
    router.push({ name: 'campaigns_whatsapp_templates' });
  }
};

// NOTICE: KAPTION-INTEGRATION — Convert emoji shortcodes as user types
const onBodyInput = () => {
  const converted = convertEmojiShortcodes(form.body);
  if (converted !== form.body) {
    const el = bodyTextarea.value;
    // Shortcode `:xyz:` (5+ chars) becomes a single emoji (1-2 chars)
    // Adjust cursor by the length difference so it lands after the emoji
    const cursorBefore = el?.selectionStart || 0;
    const delta = form.body.length - converted.length;
    form.body = converted;
    nextTick(() => {
      const pos = Math.max(0, cursorBefore - delta);
      el?.setSelectionRange(pos, pos);
    });
  }
};

const submitForm = async () => {
  if (!isFormValid.value) return;

  try {
    const templateData = {
      name: form.name,
      language: form.language,
      body: convertEmojiShortcodes(form.body),
      header_type: form.headerType,
      header_text: form.headerText || undefined,
      header_media_url: form.headerMediaUrl || undefined,
      footer: form.footer || undefined,
      buttons: form.buttons.length
        ? form.buttons.map(b => ({
            type: b.type,
            text: b.text,
            url: b.url || undefined,
            phone_number: b.phone_number || undefined,
          }))
        : undefined,
    };

    await store.dispatch('whatsappTemplates/create', {
      inboxId: selectedInboxId.value,
      templateData,
    });

    // NOTICE: KAPTION-INTEGRATION
    // Refresh the inboxes store so the newly inserted template is available
    // in dropdowns that read from getDirectMessageTemplates (e.g. Direct Send).
    await store.dispatch('inboxes/get');

    useAlert(t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUCCESS'));
    const returnTo = route.query.returnTo;
    if (returnTo === 'direct_create') {
      router.push({
        name: 'campaigns_direct_create',
        query: { inboxId: selectedInboxId.value },
      });
    } else if (returnTo === 'direct_send') {
      router.push({
        name: 'campaigns_direct_send',
        query: { inboxId: selectedInboxId.value },
      });
    } else {
      router.push({ name: 'campaigns_whatsapp_templates' });
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.error || error.message || 'Unknown error';
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERROR', { error: errorMsg }));
  }
};

// Auto-select inbox: prefer query param, fall back to first Cloud inbox
watch(
  whatsAppCloudInboxes,
  inboxes => {
    if (inboxes.length > 0 && !selectedInboxId.value) {
      const qInboxId = route.query.inboxId;
      selectedInboxId.value = qInboxId ? Number(qInboxId) : inboxes[0].id;
    }
  },
  { immediate: true }
);

// NOTICE: KAPTION-INTEGRATION — await so the watcher on whatsAppCloudInboxes
// fires AFTER inboxes are in the store, fixing the race condition when
// navigating from Direct Messages.
onMounted(async () => {
  await store.dispatch('inboxes/get');
});
</script>

<template>
  <div
    class="wp-card wp-campaign-fullpage flex flex-col h-full bg-n-background -mx-6"
  >
    <!-- Header -->
    <header
      class="relative flex items-center px-8 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="handleCancel"
      >
        <span class="i-lucide-arrow-left w-5 h-5" />
        <span class="text-sm font-medium">
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE_PAGE.BACK') }}
        </span>
      </button>
      <h1
        class="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-n-slate-12"
      >
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE_PAGE.TITLE') }}
      </h1>
    </header>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto">
      <!-- No Inbox State -->
      <div
        v-if="hasNoInbox"
        class="flex flex-col items-center justify-center py-16 gap-3"
      >
        <Icon
          icon="i-lucide-message-square-warning"
          class="size-10 text-n-slate-9"
        />
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATE_MGMT.NO_INBOX.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATE_MGMT.NO_INBOX.DESCRIPTION') }}
        </p>
      </div>

      <!-- Form + Preview Layout -->
      <div v-else class="flex h-full pr-8">
        <!-- Form (left) -->
        <div class="flex-1 p-8 overflow-y-auto space-y-4">
          <!-- Inbox Selector (if multiple) -->
          <div v-if="hasMultipleInboxes">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.INBOX_SELECTOR.LABEL') }}
            </label>
            <select
              v-model="selectedInboxId"
              class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand cursor-pointer"
            >
              <option
                v-for="inbox in whatsAppCloudInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }} ({{ inbox.phone_number }})
              </option>
            </select>
          </div>

          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.NAME_LABEL') }}
            </label>
            <input
              v-model="form.name"
              type="text"
              :maxlength="LIMITS.NAME_MAX"
              :placeholder="
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.NAME_PLACEHOLDER')
              "
              class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
            <p v-if="nameError" class="text-xs text-n-ruby-11 mt-1">
              {{ nameError }}
            </p>
            <p v-else class="text-xs text-n-slate-10 mt-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.NAME_HELP') }}
            </p>
          </div>

          <!-- Language -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.LANGUAGE_LABEL') }}
            </label>
            <select
              v-model="form.language"
              class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand cursor-pointer"
            >
              <option
                v-for="lang in LANGUAGES"
                :key="lang.code"
                :value="lang.code"
              >
                {{
                  t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.LANGUAGE_DISPLAY', {
                    label: lang.label,
                    code: lang.code,
                  })
                }}
              </option>
            </select>
          </div>

          <!-- Header -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_LABEL') }}
            </label>
            <select
              v-model="form.headerType"
              class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand cursor-pointer mb-2"
            >
              <option value="none">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_NONE') }}
              </option>
              <option value="text">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_TEXT') }}
              </option>
              <option value="image">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_IMAGE') }}
              </option>
              <option value="video">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_VIDEO') }}
              </option>
              <option value="document">
                {{
                  t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_DOCUMENT')
                }}
              </option>
            </select>
            <input
              v-if="form.headerType === 'text'"
              v-model="form.headerText"
              type="text"
              :maxlength="LIMITS.HEADER_TEXT_MAX"
              :placeholder="
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TEXT_PLACEHOLDER')
              "
              class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div
              v-if="form.headerType === 'text'"
              class="flex items-center justify-between mt-1"
            >
              <p
                v-if="headerTextError || headerVarsError"
                class="text-xs text-n-ruby-11"
              >
                {{ headerTextError || headerVarsError }}
              </p>
              <span v-else />
              <span
                class="text-xs tabular-nums"
                :class="
                  form.headerText.length > LIMITS.HEADER_TEXT_MAX
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-10'
                "
              >
                {{ form.headerText.length }}/{{ LIMITS.HEADER_TEXT_MAX }}
              </span>
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
            <div
              v-if="detectedHeaderParams.length > 0"
              class="flex flex-wrap gap-1.5 mt-2"
            >
              <span class="text-xs text-n-slate-10 mr-1">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.PARAMS_LABEL') }}
              </span>
              <span
                v-for="param in detectedHeaderParams"
                :key="param"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
              >
                {{ param }}
              </span>
            </div>
            <KaptionMediaUpload
              v-if="['image', 'video', 'document'].includes(form.headerType)"
              v-model="form.headerMediaUrl"
              :media-type="form.headerType"
            />
          </div>

          <!-- Body -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BODY_LABEL') }}
            </label>
            <textarea
              ref="bodyTextarea"
              v-model="form.body"
              :maxlength="LIMITS.BODY_MAX"
              :placeholder="
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BODY_PLACEHOLDER')
              "
              rows="5"
              class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand resize-y"
              @input="onBodyInput"
            />
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div class="flex items-center justify-between mt-1">
              <p
                v-if="bodyError || bodyVarsError || bodyVarsPositionError"
                class="text-xs text-n-ruby-11"
              >
                {{ bodyError || bodyVarsError || bodyVarsPositionError }}
              </p>
              <p v-else class="text-xs text-n-slate-10">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BODY_HELP') }}
              </p>
              <span
                class="text-xs tabular-nums flex-shrink-0 ml-2"
                :class="
                  form.body.length > LIMITS.BODY_MAX
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-10'
                "
              >
                {{ form.body.length }}/{{ LIMITS.BODY_MAX }}
              </span>
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
            <div
              v-if="detectedBodyParams.length > 0"
              class="flex flex-wrap gap-1.5 mt-2"
            >
              <span class="text-xs text-n-slate-10 mr-1">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.PARAMS_LABEL') }}
              </span>
              <span
                v-for="param in detectedBodyParams"
                :key="param"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
              >
                {{ param }}
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FOOTER_LABEL') }}
            </label>
            <input
              v-model="form.footer"
              type="text"
              :maxlength="LIMITS.FOOTER_MAX"
              :placeholder="
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FOOTER_PLACEHOLDER')
              "
              class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div class="flex items-center justify-between mt-1">
              <p v-if="footerError" class="text-xs text-n-ruby-11">
                {{ footerError }}
              </p>
              <span v-else />
              <span
                v-if="form.footer"
                class="text-xs tabular-nums"
                :class="
                  form.footer.length > LIMITS.FOOTER_MAX
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-10'
                "
              >
                {{ form.footer.length }}/{{ LIMITS.FOOTER_MAX }}
              </span>
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
          </div>

          <!-- Buttons -->
          <div>
            <div class="flex justify-between items-center mb-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTONS_LABEL') }}
              </label>
              <NextButton
                v-if="form.buttons.length < 3"
                xs
                faded
                @click="addButton"
              >
                <Icon icon="i-lucide-plus" class="size-3 mr-1" />
                {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.ADD_BUTTON') }}
              </NextButton>
            </div>

            <div
              v-for="(button, index) in form.buttons"
              :key="index"
              class="flex gap-2 items-start mb-2 p-3 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak"
            >
              <div class="flex-1 space-y-2">
                <select
                  v-model="button.type"
                  class="w-full h-8 px-2 text-xs rounded bg-n-background text-n-slate-12 outline outline-1 outline-n-weak cursor-pointer"
                >
                  <option value="QUICK_REPLY">
                    {{
                      t(
                        'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_QUICK_REPLY'
                      )
                    }}
                  </option>
                  <option value="URL">
                    {{
                      t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_URL')
                    }}
                  </option>
                  <option value="PHONE_NUMBER">
                    {{
                      t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_PHONE')
                    }}
                  </option>
                </select>
                <div>
                  <input
                    v-model="button.text"
                    type="text"
                    :maxlength="LIMITS.BUTTON_TEXT_MAX"
                    :placeholder="
                      t(
                        'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TEXT_PLACEHOLDER'
                      )
                    "
                    class="w-full h-8 px-2 text-xs rounded bg-n-background text-n-slate-12 outline outline-1 outline-n-weak"
                  />
                  <p
                    v-if="buttonErrors[index]?.text"
                    class="text-[11px] text-n-ruby-11 mt-0.5"
                  >
                    {{ buttonErrors[index].text }}
                  </p>
                </div>
                <div v-if="button.type === 'URL'">
                  <input
                    v-model="button.url"
                    type="url"
                    :maxlength="LIMITS.BUTTON_URL_MAX"
                    :placeholder="
                      t(
                        'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_URL_PLACEHOLDER'
                      )
                    "
                    class="w-full h-8 px-2 text-xs rounded bg-n-background text-n-slate-12 outline outline-1 outline-n-weak"
                  />
                  <p
                    v-if="buttonErrors[index]?.url"
                    class="text-[11px] text-n-ruby-11 mt-0.5"
                  >
                    {{ buttonErrors[index].url }}
                  </p>
                </div>
                <div v-if="button.type === 'PHONE_NUMBER'">
                  <input
                    v-model="button.phone_number"
                    type="tel"
                    :maxlength="LIMITS.BUTTON_PHONE_MAX"
                    :placeholder="
                      t(
                        'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_PHONE_PLACEHOLDER'
                      )
                    "
                    class="w-full h-8 px-2 text-xs rounded bg-n-background text-n-slate-12 outline outline-1 outline-n-weak"
                  />
                  <p
                    v-if="buttonErrors[index]?.phone"
                    class="text-[11px] text-n-ruby-11 mt-0.5"
                  >
                    {{ buttonErrors[index].phone }}
                  </p>
                </div>
              </div>
              <button
                class="p-1.5 rounded hover:bg-n-ruby-3 text-n-slate-11 hover:text-n-ruby-11"
                @click="removeButton(index)"
              >
                <Icon icon="i-lucide-trash-2" class="size-3.5" />
              </button>
            </div>
          </div>
        </div>

        <!-- Preview (right) -->
        <div
          class="w-80 flex-shrink-0 border-l border-n-weak flex flex-col overflow-hidden"
        >
          <div class="px-4 py-3 border-b border-n-weak">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.TITLE') }}
            </h4>
          </div>
          <KaptionWhatsAppPreviewEnhanced
            :template="hasPreviewContent ? previewTemplate : null"
            :variables="[]"
            :resolved-values="{}"
            mode="render"
            :loading="false"
            :bare="false"
            truncate
            company-name="Business"
            class="w-full flex-1"
          />
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      v-if="!hasNoInbox"
      class="flex justify-end gap-3 px-8 py-4 border-t border-n-weak bg-n-solid-2"
    >
      <NextButton faded @click="handleCancel">
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.CANCEL') }}
      </NextButton>
      <NextButton
        :disabled="!isFormValid"
        :is-loading="uiFlags.isCreating"
        @click="submitForm"
      >
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUBMIT') }}
      </NextButton>
    </footer>
  </div>
</template>
